import type {
  Dimension,
  Mat2,
  Mat3,
  MatrixFor,
  Vec2,
  Vec3,
  VectorFor,
} from "./types";

export function matrix<D extends Dimension>(
  dimension: D,
  values: readonly unknown[],
): MatrixFor<D> {
  assertTuple(values, dimension ** 2);
  return (
    dimension === 2
      ? [values[0], values[1], values[2], values[3]]
      : [
          values[0],
          values[1],
          values[2],
          values[3],
          values[4],
          values[5],
          values[6],
          values[7],
          values[8],
        ]
  ) as MatrixFor<D>;
}

export function vector<D extends Dimension>(
  dimension: D,
  values: readonly unknown[],
): VectorFor<D> {
  assertTuple(values, dimension);
  return (
    dimension === 2 ? [values[0], values[1]] : [values[0], values[1], values[2]]
  ) as VectorFor<D>;
}

export function copyMatrix<D extends Dimension>(
  dimension: D,
  values: Readonly<MatrixFor<D>>,
): MatrixFor<D> {
  return matrix(dimension, values);
}

export function copyVector<D extends Dimension>(
  dimension: D,
  values: Readonly<VectorFor<D>>,
): VectorFor<D> {
  return vector(dimension, values);
}

export function identity<D extends Dimension>(dimension: D): MatrixFor<D> {
  return matrix(
    dimension,
    dimension === 2 ? [1, 0, 0, 1] : [1, 0, 0, 0, 1, 0, 0, 0, 1],
  );
}

export function multiply<D extends Dimension>(
  dimension: D,
  left: Readonly<MatrixFor<D>>,
  right: Readonly<MatrixFor<D>>,
): MatrixFor<D> {
  if (dimension === 2) {
    assertTuple(left, 4);
    assertTuple(right, 4);
    const result: Mat2 = [
      left[0] * right[0] + left[1] * right[2],
      left[0] * right[1] + left[1] * right[3],
      left[2] * right[0] + left[3] * right[2],
      left[2] * right[1] + left[3] * right[3],
    ];
    assertTuple(result, 4);
    return result as MatrixFor<D>;
  }

  assertTuple(left, 9);
  assertTuple(right, 9);
  const result: Mat3 = [
    left[0] * right[0] + left[1] * right[3] + left[2] * right[6],
    left[0] * right[1] + left[1] * right[4] + left[2] * right[7],
    left[0] * right[2] + left[1] * right[5] + left[2] * right[8],
    left[3] * right[0] + left[4] * right[3] + left[5] * right[6],
    left[3] * right[1] + left[4] * right[4] + left[5] * right[7],
    left[3] * right[2] + left[4] * right[5] + left[5] * right[8],
    left[6] * right[0] + left[7] * right[3] + left[8] * right[6],
    left[6] * right[1] + left[7] * right[4] + left[8] * right[7],
    left[6] * right[2] + left[7] * right[5] + left[8] * right[8],
  ];
  assertTuple(result, 9);
  return result as MatrixFor<D>;
}

export function compose<D extends Dimension>(
  dimension: D,
  values: readonly Readonly<MatrixFor<D>>[],
): MatrixFor<D> {
  return values.reduce<MatrixFor<D>>(
    (total, value) => multiply(dimension, total, value),
    identity(dimension),
  );
}

export function transform<D extends Dimension>(
  dimension: D,
  transform: Readonly<MatrixFor<D>>,
  values: Readonly<VectorFor<D>>,
): VectorFor<D> {
  if (dimension === 2) {
    assertTuple(transform, 4);
    assertTuple(values, 2);
    const result: Vec2 = [
      transform[0] * values[0] + transform[1] * values[1],
      transform[2] * values[0] + transform[3] * values[1],
    ];
    assertTuple(result, 2);
    return result as VectorFor<D>;
  }

  assertTuple(transform, 9);
  assertTuple(values, 3);
  const result: Vec3 = [
    transform[0] * values[0] +
      transform[1] * values[1] +
      transform[2] * values[2],
    transform[3] * values[0] +
      transform[4] * values[1] +
      transform[5] * values[2],
    transform[6] * values[0] +
      transform[7] * values[1] +
      transform[8] * values[2],
  ];
  assertTuple(result, 3);
  return result as VectorFor<D>;
}

function assertTuple(
  values: readonly unknown[],
  length: 2,
): asserts values is Readonly<Vec2>;
function assertTuple(
  values: readonly unknown[],
  length: 3,
): asserts values is Readonly<Vec3>;
function assertTuple(
  values: readonly unknown[],
  length: 4,
): asserts values is Readonly<Mat2>;
function assertTuple(
  values: readonly unknown[],
  length: 9,
): asserts values is Readonly<Mat3>;
function assertTuple(
  values: readonly unknown[],
  length: number,
): asserts values is readonly number[];
function assertTuple(
  values: readonly unknown[],
  length: number,
): asserts values is readonly number[] {
  if (values.length !== length) throw new Error("Invalid numeric tuple");
  for (let index = 0; index < length; index += 1) {
    const value = values[index];
    if (typeof value !== "number" || !Number.isFinite(value))
      throw new Error("Invalid numeric tuple");
  }
}
